import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CONFIG } from 'variables/config';
import { MdContentCopy, MdCheck, MdOutlineVpnKey, MdOutlineDescription } from 'react-icons/md';
import ScopeBuilder, { describeScope } from 'components/apiKeyScope/ScopeBuilder';
import { useIndustries } from 'services/industryService';

interface ScopeRequest {
    _id: string;
    requestedScope: string[];
    message?: string;
    status: 'pending' | 'accepted' | 'rejected';
    requestedAt: string;
    adminNote?: string;
}

interface ApiKeyMeta {
    prefix: string;
    scope: string[];
    status: 'active' | 'revoked';
    createdAt: string;
    lastUsedAt: string | null;
    scopeRequests: ScopeRequest[];
}

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_LABELS: Record<ScopeRequest['status'], string> = {
    pending: 'En attente',
    accepted: 'Acceptée',
    rejected: 'Refusée',
};

const STATUS_CLASSES: Record<ScopeRequest['status'], string> = {
    pending: 'bg-leadrs-yellow-soft text-leadrs-yellow-focus',
    accepted: 'bg-leadrs-green-soft text-leadrs-green-focus',
    rejected: 'bg-leadrs-red-soft text-leadrs-red-focus',
};

const ApiKeyPanel = () => {
    const [apiKey, setApiKey] = useState<ApiKeyMeta | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [revealedKey, setRevealedKey] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'regenerate' | 'revoke' | null>(null);

    const [requestedScope, setRequestedScope] = useState<string[]>([]);
    const [requestMessage, setRequestMessage] = useState('');
    const [requestBusy, setRequestBusy] = useState(false);
    const [requestError, setRequestError] = useState('');

    const { industries } = useIndustries();

    const load = () => {
        setLoading(true);
        axios
            .get(`${CONFIG.apiUrl}/account/api-key`, authHeaders())
            .then((res) => setApiKey(res.data?.apiKey ?? null))
            .catch(() => setError('Impossible de charger votre clé API.'))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const handleCopy = () => {
        if (!revealedKey) return;
        navigator.clipboard.writeText(revealedKey).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleCreate = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/account/api-key`, {}, authHeaders());
            setRevealedKey(res.data.apiKey);
            load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erreur lors de la création de la clé API.');
        } finally {
            setBusy(false);
        }
    };

    const handleRegenerate = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/account/api-key/regenerate`, {}, authHeaders());
            setRevealedKey(res.data.apiKey);
            load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erreur lors de la régénération de la clé API.');
        } finally {
            setBusy(false);
            setConfirmAction(null);
        }
    };

    const handleRevoke = async () => {
        setBusy(true);
        setError('');
        try {
            await axios.delete(`${CONFIG.apiUrl}/account/api-key`, authHeaders());
            setRevealedKey(null);
            setApiKey(null);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Erreur lors de la suppression de la clé API.');
        } finally {
            setBusy(false);
            setConfirmAction(null);
        }
    };

    const handleSubmitScopeRequest = async () => {
        setRequestBusy(true);
        setRequestError('');
        try {
            await axios.post(
                `${CONFIG.apiUrl}/account/api-key/scope-requests`,
                { requestedScope, message: requestMessage },
                authHeaders()
            );
            setRequestedScope([]);
            setRequestMessage('');
            load();
        } catch (e: any) {
            setRequestError(e?.response?.data?.message || 'Erreur lors de l\'envoi de la demande.');
        } finally {
            setRequestBusy(false);
        }
    };

    const hasPendingRequest = apiKey?.scopeRequests?.some((r) => r.status === 'pending');

    return (
        <div className="flex flex-col gap-4">
            <Link
                to="/admin/api-docs"
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-leadrs-purple-focus bg-leadrs-purple-soft px-5 py-4 transition duration-200 hover:opacity-90"
            >
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white">
                        <MdOutlineDescription className="h-5 w-5 text-leadrs-brand" />
                    </div>
                    <div>
                        <p className="font-geist text-sm font-semibold text-leadrs-text">Documentation de l'API</p>
                        <p className="mt-0.5 font-geist text-xs text-leadrs-text-soft">
                            Exemples curl, fetch et testeur interactif pour intégrer la Bourse du Lead.
                        </p>
                    </div>
                </div>
                <span className="flex-shrink-0 rounded-lg bg-leadrs-brand px-4 py-2 font-geist text-sm font-medium text-white">
                    Voir la documentation
                </span>
            </Link>

            <div className="rounded-xl border border-leadrs-stroke bg-white px-5 py-5">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-purple-soft">
                        <MdOutlineVpnKey className="h-5 w-5 text-leadrs-brand" />
                    </div>
                    <div>
                        <h3 className="font-geist text-base font-semibold text-leadrs-text">Clé API - Bourse du Lead</h3>
                        <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                            Intégrez la Bourse du Lead depuis un script ou un outil externe. Par défaut, la clé n'a
                            aucun droit : demandez le scope dont vous avez besoin ci-dessous, un administrateur le
                            validera.
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mt-4 rounded-lg bg-leadrs-red-soft px-4 py-3 font-geist text-sm text-leadrs-red-focus">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="mt-4 py-6 text-center font-geist text-sm text-leadrs-text-soft">Chargement...</div>
                ) : revealedKey ? (
                    <div className="mt-4 rounded-lg border border-leadrs-yellow-focus bg-leadrs-yellow-soft px-4 py-4">
                        <p className="font-geist text-sm font-semibold text-leadrs-text">
                            Copiez cette clé maintenant, elle ne sera plus affichée en entier.
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                            <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md bg-white px-3 py-2 font-mono text-xs text-leadrs-text">
                                {revealedKey}
                            </code>
                            <button
                                onClick={handleCopy}
                                className="flex items-center gap-1.5 rounded-md bg-leadrs-brand px-3 py-2 font-geist text-xs font-medium text-white hover:opacity-90"
                            >
                                {copied ? <MdCheck className="h-4 w-4" /> : <MdContentCopy className="h-4 w-4" />}
                                {copied ? 'Copié' : 'Copier'}
                            </button>
                        </div>
                        <button
                            onClick={() => setRevealedKey(null)}
                            className="mt-3 font-geist text-xs text-leadrs-text-soft underline hover:text-leadrs-text"
                        >
                            J'ai copié ma clé
                        </button>
                    </div>
                ) : !apiKey ? (
                    <div className="mt-4">
                        <button
                            onClick={handleCreate}
                            disabled={busy}
                            className="linear rounded-lg bg-leadrs-brand px-4 py-2 font-geist text-sm font-medium text-white transition duration-200 hover:cursor-pointer disabled:opacity-50"
                        >
                            {busy ? 'Création...' : 'Créer une clé API'}
                        </button>
                    </div>
                ) : (
                    <div className="mt-4 flex flex-col gap-3">
                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3">
                            <div>
                                <p className="font-mono text-sm text-leadrs-text">{apiKey.prefix}••••••••••••••••••••</p>
                                <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                                    Statut : {apiKey.status === 'active' ? 'Active' : 'Désactivée'} · Dernière utilisation :{' '}
                                    {apiKey.lastUsedAt ? new Date(apiKey.lastUsedAt).toLocaleString('fr-FR') : 'jamais'}
                                </p>
                            </div>
                            {confirmAction ? (
                                <div className="flex items-center gap-2">
                                    <span className="font-geist text-xs text-leadrs-text-soft">
                                        {confirmAction === 'regenerate'
                                            ? "Régénérer ? L'ancienne clé cessera de fonctionner immédiatement."
                                            : 'Révoquer définitivement cette clé ?'}
                                    </span>
                                    <button
                                        onClick={confirmAction === 'regenerate' ? handleRegenerate : handleRevoke}
                                        disabled={busy}
                                        className="rounded-md bg-leadrs-red-focus px-3 py-1.5 font-geist text-xs font-medium text-white disabled:opacity-50"
                                    >
                                        {busy ? '...' : 'Confirmer'}
                                    </button>
                                    <button
                                        onClick={() => setConfirmAction(null)}
                                        className="rounded-md bg-leadrs-bg px-3 py-1.5 font-geist text-xs font-medium text-leadrs-text-soft hover:opacity-80"
                                    >
                                        Annuler
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setConfirmAction('regenerate')}
                                        className="rounded-md border border-leadrs-stroke px-3 py-1.5 font-geist text-xs font-medium text-leadrs-text hover:bg-leadrs-bg"
                                    >
                                        Régénérer
                                    </button>
                                    <button
                                        onClick={() => setConfirmAction('revoke')}
                                        className="rounded-md border border-leadrs-red-focus px-3 py-1.5 font-geist text-xs font-medium text-leadrs-red-focus hover:bg-leadrs-red-soft"
                                    >
                                        Révoquer
                                    </button>
                                </div>
                            )}
                        </div>

                        <div>
                            <p className="font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">
                                Scope accordé
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {apiKey.scope.length === 0 ? (
                                    <span className="font-geist text-sm text-leadrs-text-soft">Aucun droit accordé pour l'instant.</span>
                                ) : (
                                    apiKey.scope.map((s) => (
                                        <span
                                            key={s}
                                            className="rounded-full bg-leadrs-purple-soft px-3 py-1 font-geist text-xs font-medium text-leadrs-brand"
                                        >
                                            {describeScope(s)}
                                        </span>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {apiKey && !revealedKey && (
                <div className="rounded-xl border border-leadrs-stroke bg-white px-5 py-5">
                    <h3 className="font-geist text-base font-semibold text-leadrs-text">Demander un scope</h3>
                    <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                        Sélectionnez les droits dont vous avez besoin. Un administrateur devra valider votre demande.
                    </p>

                    {hasPendingRequest ? (
                        <div className="mt-4 rounded-lg bg-leadrs-yellow-soft px-4 py-3 font-geist text-sm text-leadrs-yellow-focus">
                            Une demande est déjà en attente de traitement.
                        </div>
                    ) : (
                        <div className="mt-4 flex flex-col gap-3">
                            <ScopeBuilder industries={industries} value={requestedScope} onChange={setRequestedScope} />
                            <textarea
                                value={requestMessage}
                                onChange={(e) => setRequestMessage(e.target.value)}
                                placeholder="Message à l'attention de l'équipe Leadrs (optionnel)"
                                className="rounded-lg border border-leadrs-stroke px-3 py-2 font-geist text-sm text-leadrs-text"
                                rows={2}
                            />
                            {requestError && (
                                <p className="font-geist text-sm text-leadrs-red-focus">{requestError}</p>
                            )}
                            <div>
                                <button
                                    onClick={handleSubmitScopeRequest}
                                    disabled={requestBusy || requestedScope.length === 0}
                                    className="linear rounded-lg bg-leadrs-brand px-4 py-2 font-geist text-sm font-medium text-white transition duration-200 hover:cursor-pointer disabled:opacity-50"
                                >
                                    {requestBusy ? 'Envoi...' : 'Envoyer la demande'}
                                </button>
                            </div>
                        </div>
                    )}

                    {apiKey.scopeRequests?.length > 0 && (
                        <div className="mt-5">
                            <p className="font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">
                                Historique des demandes
                            </p>
                            <div className="mt-2 flex flex-col gap-2">
                                {apiKey.scopeRequests.map((r) => (
                                    <div key={r._id} className="rounded-lg border border-leadrs-stroke px-3 py-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex flex-wrap gap-1.5">
                                                {r.requestedScope.map((s) => (
                                                    <span key={s} className="font-geist text-xs text-leadrs-text">
                                                        {describeScope(s)}
                                                    </span>
                                                ))}
                                            </div>
                                            <span className={`rounded-full px-2 py-0.5 font-geist text-xs font-medium ${STATUS_CLASSES[r.status]}`}>
                                                {STATUS_LABELS[r.status]}
                                            </span>
                                        </div>
                                        <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                                            {new Date(r.requestedAt).toLocaleString('fr-FR')}
                                        </p>
                                        {r.message && <p className="mt-1 font-geist text-xs text-leadrs-text-soft">{r.message}</p>}
                                        {r.status === 'rejected' && r.adminNote && (
                                            <p className="mt-1 font-geist text-xs text-leadrs-red-focus">Note : {r.adminNote}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ApiKeyPanel;
